import React, { useState, useEffect } from 'react';
import axios from 'axios';
import VideoCard from '../components/VideoCard';
import { Loader2, TrendingUp, Flame } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Trending = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrending();
  }, []);

  const fetchTrending = async () => {
    try {
      const response = await axios.get(`${API}/content/trending`);
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Error fetching trending:', error);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 lg:px-16 py-8" data-testid="trending-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Flame className="w-10 h-10 text-orange-500" />
            <h1 
              className="text-4xl md:text-6xl font-normal tracking-wide uppercase"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              Trending Now
            </h1>
          </div>
          <p className="text-gray-400">The most viewed content right now</p>
        </div>

        {/* Video Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-20" data-testid="no-trending">
            <p className="text-gray-400 text-lg">No trending videos available</p>
          </div>
        ) : (
          <>
            {/* Featured/Top Trending */}
            {videos.length > 0 && (
              <div className="mb-12">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-gray-400 uppercase tracking-wider">#1 Trending</span>
                </div>
                <div 
                  className="relative aspect-video md:aspect-[21/9] bg-[#121212] rounded-md overflow-hidden cursor-pointer group"
                  onClick={() => window.location.href = `/video/${videos[0].id}`}
                  data-testid="top-trending"
                >
                  <img 
                    src={videos[0].thumbnail || 'https://images.unsplash.com/photo-1721040530907-646692053af8?w=1920'}
                    alt={videos[0].title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                    <h2 
                      className="text-2xl md:text-4xl font-normal tracking-wide uppercase text-white mb-2"
                      style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                    >
                      {videos[0].title}
                    </h2>
                    <div className="flex items-center gap-4 text-gray-300 text-sm">
                      <span>{videos[0].views} views</span>
                      <span>{videos[0].duration}</span>
                      {videos[0].rating && <span className="text-green-500">{videos[0].rating}%</span>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Rest of trending */}
            <div className="video-grid" data-testid="trending-grid">
              {videos.slice(1).map((video, index) => (
                <div key={video.id || index} className="relative">
                  <div className="absolute -top-3 -left-3 w-8 h-8 bg-white text-black rounded-full flex items-center justify-center text-sm font-bold z-10">
                    {index + 2}
                  </div>
                  <VideoCard video={video} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Trending;
